import { Link } from "@tanstack/react-router";
import { Store } from "lucide-react";

import { Img, Price, fenInt } from "./ui.tsx";
import { Card } from "@/components/ui/card";
import { readPurchaseAs, readViewAs } from "../lib/identity.ts";
import { cn } from "@/lib/utils";

/**
 * ═══════════════════════════════════════════════════════════════
 *  商品瀑布流
 * ═══════════════════════════════════════════════════════════════
 *
 * ⚠️ **双列瀑布流，不是等高网格。**
 *    建材的图有横有竖（瓷砖是方的、花洒是竖的、地板是横的），
 *    统一裁成正方形会把花色和器型切掉 —— 而那恰恰是买家要看的东西。
 *    用 CSS `columns` 实现，不用 JS 测高。
 *
 * 🔴 **卡片上要显示「谁在卖」。** 这是这个平台跟普通商城最大的不同：
 *    同一件货在不同的店里价格不同。不标店名的话，用户从别人分享的链接
 *    点进来看到的价跟自己平时看到的不一样，只会觉得是乱标价。
 */

export interface ProductCardData {
  id: number;
  title: string;
  imageUrls: string[];
  fromPrice: number;
  /**
   * 平台指导零售价（吊牌价）。
   * 🔴 有归属的人要**同时看到两个价**：吊牌价划掉 + 他的专属价。
   *    只显示一个数的话他不知道"因为挂在王师傅名下所以便宜了 ¥60" ——
   *    而那正是他不去别处买的理由。
   * ⚠️ 服务端已经保证它比实付价高，等于或更低时回 null。
   */
  listPrice?: number | null;
  salesCount: number;
}

export function ProductGrid({
  items,
  /** 这些商品是在谁的店里看到的。不传就是"平台通用视角"。 */
  shop,
  /** 🔴 访问者自己的归属节点 —— 有归属时价格按他的算，要标出来。 */
  myShopName,
}: {
  items: readonly ProductCardData[];
  shop?: { id: number; name: string } | null;
  myShopName?: string | null;
}) {
  /**
   * ⚠️ 直接读存起来的采购身份，不让每个调用方传 ——
   *    三个页面在用这个组件，漏一个就会出现"这一页标进货价、那一页标零售价"。
   *    取数入口用的也是这个值，两边同一个来源才不会打架。
   */
  const orgId = readPurchaseAs();
  /** 我买的价（上级给我的）。 */
  const purchasing = orgId !== null && readViewAs() === "purchase";
  /** 我卖的价（客户看到的）—— 预览态。 */
  const previewing = orgId !== null && readViewAs() === "shop";

  return (
    /**
     * ⚠️ `columns-2` 的子元素要 `break-inside-avoid`，
     *    否则一张卡会被从中间劈开分到两列上。
     */
    <div className="columns-2 gap-2.5 [column-fill:balance]">
      {items.map((p) => (
        <Link
          key={p.id}
          to="/products/$id"
          params={{ id: String(p.id) }}
          search={(shop ? { shopId: shop.id } : {}) as never}
          className="mb-2.5 block break-inside-avoid"
        >
          <Card className="overflow-hidden">
            {/*
              ⚠️ 不设 aspect —— 图多高就多高，这正是瀑布流的意义。
                 但要给 min-h 兜底，否则图还没加载时卡片会瘪成一条线、
                 加载完整页跳一下。
            */}
            <Img
              src={p.imageUrls[0]}
              alt={p.title}
              className="min-h-32 w-full bg-secondary object-cover"
            />
            <div className="px-2.5 pt-2 pb-2.5">
              <p className="line-clamp-2 text-[13px] leading-snug">{p.title}</p>

              {/*
                标签行 —— 淘宝在这里放「天猫」「百亿补贴」，
                我们放**店铺**和**这是不是你的专属价**。
              */}
              <div className="mt-1.5 flex flex-wrap items-center gap-1">
                {shop && (
                  <span className="inline-flex max-w-full items-center gap-0.5 rounded bg-brand-50 px-1 py-0.5 text-[10px] text-brand-700">
                    <Store className="size-2.5 shrink-0" />
                    <span className="truncate">{shop.name}</span>
                  </span>
                )}
                {!shop && myShopName && (
                  <span className="rounded bg-brand-50 px-1 py-0.5 text-[10px] text-brand-700">
                    你的专属价
                  </span>
                )}
                {/*
                  🔴 **不在每张卡上标「进货价」。** 老板：「进货价这些内容不要显示」。
                     一屏三十件商品就是三十个一模一样的角标 —— 而"现在看到的是哪个价"
                     页面顶部那条横幅（`PurchaseBanner`）已经说过一次了，
                     每张卡再标一遍纯属噪音。
                  ⚠️ 「你的专属价」留着：那不是在解释当前是什么模式，
                     是在告诉客户"挂在这家店名下比外面便宜"，一句都没有别的地方说。
                */}
                {!shop && !myShopName && previewing && (
                  <span className="rounded bg-sky-100 px-1 py-0.5 text-[10px] text-sky-800">
                    客户看到的价
                  </span>
                )}
                {/*
                  🔴 **游客不标「平台指导价」。**
                     早先这里给没有归属的人标了这四个字，用意是"让他知道找熟人更便宜"——
                     但对一个刚进来的客户，这个词既看不懂、又在暗示"你现在这个价最贵"，
                     等于把平台的定价机制讲给他听。老板的原话：
                     **那是我的运营模式，说了我怎么赚钱。**
                     所以游客这一档**不标任何东西**；「进货价」「客户看到的价」
                     是给商家分辨自己在看哪一档的，保留。
                */}
              </div>

              <div className="mt-1.5 flex items-baseline justify-between gap-1">
                <span className="min-w-0 text-brand-600">
                  <Price cents={p.fromPrice} className="text-[18px]" />
                  <span className="ml-0.5 text-[11px] text-muted-foreground">起</span>
                  {/*
                    🔴 划线的吊牌价 —— 让他看见"便宜了多少"。
                       ⚠️ 服务端保证只有比实付价高时才给，所以这里不用再判。
                  */}
                  {p.listPrice != null && (
                    <span className="ml-1 text-[11px] text-muted-foreground line-through">
                      {fenInt(p.listPrice)}
                    </span>
                  )}
                </span>
                {p.salesCount > 0 && (
                  <span className="shrink-0 text-[11px] text-muted-foreground tabular-nums">
                    {p.salesCount} 人买过
                  </span>
                )}
              </div>
              {/*
                省了多少 —— 比"打几折"直观，建材客单价高，省的绝对值很可观。
                ⚠️ 进货模式下换个说法：对商家来说这不是"省了"，
                   而是**他还能赚多少**（按吊牌价卖出去的话）。
                   说成"省"会让他把进货价当成自己的售价去理解。
              */}
              {p.listPrice != null && (
                <p className="mt-1 text-[11px] text-brand-600">
                  {purchasing
                    ? "按吊牌价卖可赚 "
                    : previewing
                      ? "客户比吊牌价省 "
                      : "比平台价省 "}
                  {fenInt(p.listPrice - p.fromPrice)}
                </p>
              )}
            </div>
          </Card>
        </Link>
      ))}
    </div>
  );
}

/**
 * 「谁在卖」的条幅。商品详情页和店铺页顶部都用它。
 *
 * 🔴 **逛别人的店时必须说明价格按谁算。**
 *    藏起来的话用户会觉得"这家店怎么跟别家一个价"，
 *    或者更糟：以为自己被区别对待了。
 */
export function SellerNote({
  shopName,
  boundToName,
  isMine,
  className,
}: {
  shopName: string;
  boundToName?: string | null;
  isMine: boolean;
  className?: string;
}) {
  if (isMine) {
    return (
      <p className={cn("text-[12px] text-brand-700", className)}>
        {shopName} 是你的专属服务商，这里是你的专属价
      </p>
    );
  }
  if (boundToName) {
    return (
      <p className={cn("text-[12px] text-muted-foreground", className)}>
        你的专属服务商是 <span className="text-foreground">{boundToName}</span>，
        价格按他给你算
      </p>
    );
  }
  return (
    <p className={cn("text-[12px] text-muted-foreground", className)}>
      在这里下单，{shopName} 将成为你的专属服务商
    </p>
  );
}
